import { ChartSource, ChartType } from "constant/chart";
import { SensorType } from "constant/gateway";
import { Device } from "types/Gateways";
import { getSensorType } from "utils/sensorFunctions";

const EnergyConsumptionSAID = new Set([4, 5, 10, 15]);
// Flow volume positive accumulator said is 8
// Flow volume net accumulator said is 7
const WaterConsumptionSAID = new Set([8]);

export enum WidgetType {
    // Sensor
    SENSOR_CHART_SINGLE,
    SENSOR_CHART_MULTIPLE,
    SENSOR_CHART_AGGREGATE,
    SENSOR_PCS_HEATMAP,
    SENSOR_PCS_COMBINED_HEATMAP,
    SENSOR_CARD_ANY,
    SENSOR_CARD_TEMPERATURE,
    SENSOR_CARD_IAQ,
    SENSOR_CARD_HUMIDITY,
    SENSOR_CARD_AMBIENT,
    SENSOR_CARD_MOTION,
    SENSOR_CARD_MULTI_VALUES,
    SENSOR_DIAL_READING,
    SENSOR_DIAL_READING_GRADE,

    // Actuator
    ACTUATOR_CHART_SINGLE,
    ACTUATOR_CHART_MULTIPLE,
    ACTUATOR_CHART_CORRELATION,

    // Gateway
    GATEWAY_CARD_OVERVIEW,
    GATEWAY_TABLE_STATUS,
    GATEWAY_CARD_UPTIME_HISTORY,
    GATEWAY_CHART_STATUS_STATISTICS,
    GATEWAY_CHART_STORAGE_USAGE,

    // PSL
    PSL_TABLE_STATUS,
    PSL_CHART_SINGLE_ROOM,
    PSL_CHART_MULTIPLE_ROOM,
    PSL_CHART_ROOM_TIMELINE,
    PSL_CHART_ZONE_TIMELINE,
    PSL_CHART_GROUP_TIMELINE,

    // Meter
    METER_CHART_ENERGY_CONSUMPTION,        // BAR + SINGLE_SENSOR
    METER_CHART_ENERGY_COMBINE_CONSUMPTION, // STACKED_BAR + MULTIPLE_SENSORS
    METER_CHART_WATER_CONSUMPTION,          // BAR + SINGLE_SENSOR
    METER_CHART_WATER_COMBINE_CONSUMPTION,  // STACKED_BAR + MULTIPLE_SENSORS


    // Event
    EVENT_CHART_ALERT_TRIGGER_COUNT,
    EVENT_CHART_NOTIFIER_COUNT,

    // Subscription
    SUBSCRIPTION_CHART_TOKEN_DONUT,
    SUBSCRIPTION_CHART_TOKEN_STACKED,

    // LDSU
    LDSU_CARD_READINGS,
    LDSU_CARD_OVERVIEW_READINGS,

    // External Data
    EXTERNAL_DATA_CARD_COMBINED,
}

export type WidgetConfigType = {
    isCombined: boolean; // true: combines multiple items into one widget, false: creates separate widgets for each item (bulk creation)
    maxItems: number; // max items for combined widgets.
    minItems: number; // min items for all widgets.
    defaultWidgetName: string;
    filter?: (widgetType: WidgetType) => (d: Device) => boolean;
}

// Type for chart and source combination
type ChartSourcePair = {
    chart: ChartType;
    source: ChartSource;
};

// Shared config for multiple sensor types
const sharedSensorConfig: WidgetConfigType = {
    isCombined: false,
    maxItems: 1,
    minItems: 1,
    defaultWidgetName: "",
};

const widgetConfig: Record<WidgetType, WidgetConfigType> = {

    // Sensor Charts Config
    [WidgetType.SENSOR_CHART_SINGLE]: sharedSensorConfig,
    [WidgetType.SENSOR_CHART_MULTIPLE]: {
        isCombined: true,
        maxItems: 8,
        minItems: 2,
        defaultWidgetName: "Multiple sensor chart",
    },
    [WidgetType.SENSOR_CHART_AGGREGATE]: {
        isCombined: true,
        maxItems: 8,
        minItems: 2,
        defaultWidgetName: "People Count",
    },
    [WidgetType.SENSOR_PCS_HEATMAP]: {
        isCombined: false,
        maxItems: 8,
        minItems: 1,
        defaultWidgetName: "People Count Heatmap",
    },
    [WidgetType.SENSOR_PCS_COMBINED_HEATMAP]: {
        isCombined: true,
        maxItems: 4,
        minItems: 4,
        defaultWidgetName: "People Count",
    },
    [WidgetType.SENSOR_CARD_ANY]: {
        ...sharedSensorConfig,
        filter: () => (d: Device) => {
            if (!d.sensorType) {
                return false;
            }

            return true;
        },
    },
    [WidgetType.SENSOR_CARD_TEMPERATURE]: {
        ...sharedSensorConfig,
        filter: () => (d: Device) => {
            if (!d.sensorType) {
                return false;
            }
            return d.sensorType.toLowerCase() === "temperature sensor";
        },
    },
    [WidgetType.SENSOR_CARD_IAQ]: {
        ...sharedSensorConfig,
        filter: () => (d: Device) => {
            if (!d.sensorType) {
                return false;
            }
            return d.sensorType.toLowerCase() === "iaq sensor";
        },
    },
    [WidgetType.SENSOR_CARD_HUMIDITY]: {
        ...sharedSensorConfig,
        filter: () => (d: Device) => {
            if (!d.sensorType) {
                return false;
            }
            return d.sensorType.toLowerCase() === "humidity sensor";
        },
    },
    [WidgetType.SENSOR_CARD_AMBIENT]: {
        ...sharedSensorConfig,
        filter: () => (d: Device) => {
            if (!d.sensorType) {
                return false;
            }
            return d.sensorType.toLowerCase() === "ambient light sensor";
        },
    },
    [WidgetType.SENSOR_CARD_MOTION]: {
        ...sharedSensorConfig,
        filter: () => (d: Device) => {
            if (!d.sensorType) {
                return false;
            }
            return d.sensorType.toLowerCase() === "motion sensor";
        },
    },
    [WidgetType.SENSOR_CARD_MULTI_VALUES]: {
        isCombined: true,
        maxItems: 3,
        minItems: 2,
        defaultWidgetName: "Multi Sensor Values",
    },
    [WidgetType.SENSOR_DIAL_READING_GRADE]: {
        isCombined: false,
        maxItems: 1,
        minItems: 1,
        defaultWidgetName: "",
        filter: () => (d: Device) => d.format !== "boolean",
    },
    [WidgetType.SENSOR_DIAL_READING]: {
        isCombined: false,
        maxItems: 1,
        minItems: 1,
        defaultWidgetName: "",
        filter: () => (d: Device) => d.format !== "boolean",
    },

    // Actuator Charts Config
    [WidgetType.ACTUATOR_CHART_SINGLE]: {
        isCombined: false,
        maxItems: 8,
        minItems: 1,
        defaultWidgetName: "",
    },
    [WidgetType.ACTUATOR_CHART_MULTIPLE]: {
        isCombined: true,
        maxItems: 8,
        minItems: 2,
        defaultWidgetName: "Multiple actuator chart",
    },
    [WidgetType.ACTUATOR_CHART_CORRELATION]: {
        isCombined: true,
        maxItems: 8,
        minItems: 2,
        defaultWidgetName: "Actuator correlation",
    },

    // Gateway Charts Config
    [WidgetType.GATEWAY_CARD_OVERVIEW]: {
        isCombined: false,
        maxItems: 1,
        minItems: 1,
        defaultWidgetName: "Gateway overview",
    },
    [WidgetType.GATEWAY_TABLE_STATUS]: {
        isCombined: true,
        maxItems: 8,
        minItems: 1,
        defaultWidgetName: "Gateways with Status",
    },
    [WidgetType.GATEWAY_CARD_UPTIME_HISTORY]: {
        isCombined: true,
        maxItems: 3,
        minItems: 1,
        defaultWidgetName: "Gateway uptime over past 90 days",
    },
    [WidgetType.GATEWAY_CHART_STATUS_STATISTICS]: {
        isCombined: false,
        maxItems: 8,
        minItems: 1,
        defaultWidgetName: "",
    },
    [WidgetType.GATEWAY_CHART_STORAGE_USAGE]: {
        isCombined: false,
        maxItems: 8,
        minItems: 1,
        defaultWidgetName: "",
    },

    // PSL
    [WidgetType.PSL_TABLE_STATUS]: {
        isCombined: true,
        maxItems: 32,
        minItems: 1,
        defaultWidgetName: "Room/Zone/Group Status",
    },
    [WidgetType.PSL_CHART_SINGLE_ROOM]: {
        isCombined: false,
        maxItems: 32,
        minItems: 1,
        defaultWidgetName: "",
    },
    [WidgetType.PSL_CHART_MULTIPLE_ROOM]: {
        isCombined: true,
        maxItems: 32,
        minItems: 1,
        defaultWidgetName: "Accumulated Room Usage",
    },
    [WidgetType.PSL_CHART_ROOM_TIMELINE]: {
        isCombined: false,
        maxItems: 32,
        minItems: 1,
        defaultWidgetName: "",
    },
    [WidgetType.PSL_CHART_ZONE_TIMELINE]: {
        isCombined: false,
        maxItems: 32,
        minItems: 1,
        defaultWidgetName: "",
    },
    [WidgetType.PSL_CHART_GROUP_TIMELINE]: {
        isCombined: false,
        maxItems: 32,
        minItems: 1,
        defaultWidgetName: "",
    },

    // Meter Charts Config
    [WidgetType.METER_CHART_ENERGY_CONSUMPTION]: {
        isCombined: false,
        maxItems: 4,
        minItems: 1,
        defaultWidgetName: "Energy Consumption",
        filter: () => (d: Device) => {
            return (
                getSensorType({ CLS: d.cls }) ===
                SensorType.EnergyConsumption &&
                EnergyConsumptionSAID.has(d.said)
            );
        }
    },
    [WidgetType.METER_CHART_ENERGY_COMBINE_CONSUMPTION]: {
        isCombined: true,
        maxItems: 4,
        minItems: 2,
        defaultWidgetName: "Energy Consumption",
        filter: () => (d: Device) => {
            return (
                getSensorType({ CLS: d.cls }) ===
                SensorType.EnergyConsumption &&
                EnergyConsumptionSAID.has(d.said)
            );
        }
    },
    [WidgetType.METER_CHART_WATER_CONSUMPTION]: {
        isCombined: false,
        maxItems: 4,
        minItems: 1,
        defaultWidgetName: "Water Consumption",
        filter: () => (d: Device) => {
            return (
                getSensorType({ CLS: d.cls }) ===
                SensorType.WaterConsumption &&
                WaterConsumptionSAID.has(d.said)
            );
        }
    },
    [WidgetType.METER_CHART_WATER_COMBINE_CONSUMPTION]: {
        isCombined: true,
        maxItems: 4,
        minItems: 2,
        defaultWidgetName: "Water Consumption",
        filter: () => (d: Device) => {
            return (
                getSensorType({ CLS: d.cls }) ===
                SensorType.WaterConsumption &&
                WaterConsumptionSAID.has(d.said)
            );
        }
    },

    // LDSU
    [WidgetType.LDSU_CARD_READINGS]: {
        isCombined: false,
        maxItems: 1,
        minItems: 1,
        defaultWidgetName: "LDSU Overview",
    },
    [WidgetType.LDSU_CARD_OVERVIEW_READINGS]: {
        isCombined: false,
        maxItems: 1,
        minItems: 1,
        defaultWidgetName: "LDSU Overview",
    },

    // Event
    [WidgetType.EVENT_CHART_ALERT_TRIGGER_COUNT]: {
        isCombined: false,
        maxItems: 1,
        minItems: 0,
        defaultWidgetName: "Count of Events",
    },
    [WidgetType.EVENT_CHART_NOTIFIER_COUNT]: {
        isCombined: false,
        maxItems: 1,
        minItems: 0,
        defaultWidgetName: "Count of Email, SMS, Notification",
    },

    // Subscription
    [WidgetType.SUBSCRIPTION_CHART_TOKEN_DONUT]: {
        isCombined: false,
        maxItems: 1,
        minItems: 0,
        defaultWidgetName: "Token Spending",
    },
    [WidgetType.SUBSCRIPTION_CHART_TOKEN_STACKED]: {
        isCombined: false,
        maxItems: 1,
        minItems: 0,
        defaultWidgetName: "Token Spending",
    },

    // External Data
    [WidgetType.EXTERNAL_DATA_CARD_COMBINED]: {
        isCombined: false,
        maxItems: 1,
        minItems: 1,
        defaultWidgetName: "External Data",
    },
}

/**
 * Mapping from WidgetType to chart + source
 * When you have WidgetType, use this to get the corresponding chart and source
 */
const widgetTypeToChartSource: Record<WidgetType, ChartSourcePair> = {
    // Sensor
    [WidgetType.SENSOR_CHART_SINGLE]: {
        chart: ChartType.SINGLE_LINE,
        source: ChartSource.SINGLE_SENSOR,
    },
    [WidgetType.SENSOR_CHART_MULTIPLE]: {
        chart: ChartType.MULTIPLE_LINES,
        source: ChartSource.MULTIPLE_SENSORS,
    },
    [WidgetType.SENSOR_CHART_AGGREGATE]: {
        chart: ChartType.SINGLE_LINE,
        source: ChartSource.MULTIPLE_SENSORS,
    },
    [WidgetType.SENSOR_PCS_HEATMAP]: {
        chart: ChartType.HEATMAP,
        source: ChartSource.SINGLE_SENSOR,
    },
    [WidgetType.SENSOR_PCS_COMBINED_HEATMAP]: {
        chart: ChartType.HEATMAP,
        source: ChartSource.MULTIPLE_SENSORS,
    },
    [WidgetType.SENSOR_CARD_ANY]: {
        chart: ChartType.CARD_NUMBER_ONLY,
        source: ChartSource.SINGLE_SENSOR,
    },
    [WidgetType.SENSOR_CARD_TEMPERATURE]: {
        chart: ChartType.CARD_BAR,
        source: ChartSource.SINGLE_SENSOR,
    },
    [WidgetType.SENSOR_CARD_IAQ]: {
        chart: ChartType.CARD_BAR,
        source: ChartSource.SINGLE_SENSOR,
    },
    [WidgetType.SENSOR_CARD_HUMIDITY]: {
        chart: ChartType.CARD_BAR,
        source: ChartSource.SINGLE_SENSOR,
    },
    [WidgetType.SENSOR_CARD_AMBIENT]: {
        chart: ChartType.CARD_BAR,
        source: ChartSource.SINGLE_SENSOR,
    },
    [WidgetType.SENSOR_CARD_MOTION]: {
        chart: ChartType.CARD_TEXT,
        source: ChartSource.SINGLE_SENSOR,
    },
    [WidgetType.SENSOR_CARD_MULTI_VALUES]: {
        chart: ChartType.CARD_MULTIPLE_LINES,
        source: ChartSource.MULTIPLE_SENSORS,
    },
    [WidgetType.SENSOR_DIAL_READING]: {
        chart: ChartType.GAUGE,
        source: ChartSource.SINGLE_SENSOR,
    },
    [WidgetType.SENSOR_DIAL_READING_GRADE]: {
        chart: ChartType.GAUGE_COLOURS,
        source: ChartSource.SINGLE_SENSOR,
    },

    // Actuator
    [WidgetType.ACTUATOR_CHART_SINGLE]: {
        chart: ChartType.SINGLE_LINE,
        source: ChartSource.SINGLE_ACTUATOR,
    },
    [WidgetType.ACTUATOR_CHART_MULTIPLE]: {
        chart: ChartType.MULTIPLE_LINES,
        source: ChartSource.MULTIPLE_ACTUATORS,
    },
    [WidgetType.ACTUATOR_CHART_CORRELATION]: {
        chart: ChartType.MULTIPLE_LINES,
        source: ChartSource.SENSORS_AND_ACTUATORS,
    },

    // Gateway
    [WidgetType.GATEWAY_CARD_OVERVIEW]: {
        chart: ChartType.CARD_GATEWAY,
        source: ChartSource.SINGLE_GATEWAY,
    },
    [WidgetType.GATEWAY_TABLE_STATUS]: {
        chart: ChartType.CARD_STATUS,
        source: ChartSource.MULTIPLE_GATEWAYS,
    },
    [WidgetType.GATEWAY_CARD_UPTIME_HISTORY]: {
        chart: ChartType.CARD_MULTIPLE_BARS,
        source: ChartSource.MULTIPLE_GATEWAYS,
    },
    [WidgetType.GATEWAY_CHART_STATUS_STATISTICS]: {
        chart: ChartType.DONUT,
        source: ChartSource.SINGLE_GATEWAY,
    },
    [WidgetType.GATEWAY_CHART_STORAGE_USAGE]: {
        chart: ChartType.DONUT,
        source: ChartSource.STORAGE,
    },

    // PSL
    [WidgetType.PSL_TABLE_STATUS]: {
        chart: ChartType.CARD_STATUS,
        source: ChartSource.MULTIPLE_SENSORS,
    },
    [WidgetType.PSL_CHART_SINGLE_ROOM]: {
        chart: ChartType.BAR,
        source: ChartSource.MULTIPLE_SENSORS,
    },
    [WidgetType.PSL_CHART_MULTIPLE_ROOM]: {
        chart: ChartType.BAR,
        source: ChartSource.AGGREGATED_MULTIPLE_SENSORS,
    },
    [WidgetType.PSL_CHART_ROOM_TIMELINE]: {
        chart: ChartType.MULTIPLE_LINES,
        source: ChartSource.SINGLE_SENSOR,
    },
    [WidgetType.PSL_CHART_ZONE_TIMELINE]: {
        chart: ChartType.SINGLE_STEP_LINE,
        source: ChartSource.SINGLE_SENSOR,
    },
    [WidgetType.PSL_CHART_GROUP_TIMELINE]: {
        chart: ChartType.SINGLE_STEP_LINE,
        source: ChartSource.SINGLE_SENSOR,
    },

    // Meter
    [WidgetType.METER_CHART_ENERGY_CONSUMPTION]: {
        chart: ChartType.BAR,
        source: ChartSource.SINGLE_SENSOR,
    },
    [WidgetType.METER_CHART_ENERGY_COMBINE_CONSUMPTION]: {
        chart: ChartType.STACKED_BAR,
        source: ChartSource.MULTIPLE_SENSORS,
    },
    [WidgetType.METER_CHART_WATER_CONSUMPTION]: {
        chart: ChartType.BAR,
        source: ChartSource.SINGLE_SENSOR,
    },
    [WidgetType.METER_CHART_WATER_COMBINE_CONSUMPTION]: {
        chart: ChartType.STACKED_BAR,
        source: ChartSource.MULTIPLE_SENSORS,
    },

    // LDSU
    [WidgetType.LDSU_CARD_READINGS]: {
        chart: ChartType.CARD_MULTIPLE_LINES_TYPE3,
        source: ChartSource.SINGLE_LDSU,
    },
    [WidgetType.LDSU_CARD_OVERVIEW_READINGS]: {
        chart: ChartType.CARD_MULTIPLE_LINES_TYPE2,
        source: ChartSource.SINGLE_LDSU,
    },

    // Event
    [WidgetType.EVENT_CHART_ALERT_TRIGGER_COUNT]: {
        chart: ChartType.BAR,
        source: ChartSource.EVENT,
    },
    [WidgetType.EVENT_CHART_NOTIFIER_COUNT]: {
        chart: ChartType.STACKED_BAR,
        source: ChartSource.NOTIFIER,
    },

    // Subscription
    [WidgetType.SUBSCRIPTION_CHART_TOKEN_DONUT]: {
        chart: ChartType.DONUT,
        source: ChartSource.SUBSCRIPTION,
    },
    [WidgetType.SUBSCRIPTION_CHART_TOKEN_STACKED]: {
        chart: ChartType.STACKED_BAR,
        source: ChartSource.SUBSCRIPTION,
    },

    // External Data : Currently no used
    [WidgetType.EXTERNAL_DATA_CARD_COMBINED]: {
        chart: ChartType.CARD_MULTIPLE_LINES,
        source: ChartSource.SINGLE_SENSOR,
    },
};

/**
 * Reverse Map: "chart|source" -> WidgetType[]
 * Built once at module load - O(1) lookup
 */
const chartSourceToWidgetTypeMap = new Map<string, WidgetType>();

// Build reverse map when module loads
(() => {
    const createKey = (chart: ChartType, source: ChartSource) =>
        `${String(chart).toUpperCase()}|${String(source).toUpperCase()}`;

    // Iterate over the Record entries
    Object.entries(widgetTypeToChartSource).forEach(([key, value]) => {
        const widgetType = Number(key) as WidgetType;
        const pair = value;
        const { chart, source } = pair;
        const reverseKey = createKey(chart, source);
        chartSourceToWidgetTypeMap.set(reverseKey, widgetType);
    });
})();

export function getWidgetConfigByWidgetType(
    widgetType: WidgetType
): WidgetConfigType {
    return widgetConfig[widgetType];
}

/**
 * Get WidgetType from chart and source
 * @param chart - Chart type string or ChartType enum value (e.g., "SINGLE_LINE", ChartType.CARD_GATEWAY")
 * @param source - Source type string or ChartSource enum value (e.g., "SINGLE_SENSOR", ChartSource.MULTIPLE_GATEWAYS")
 * @returns WidgetType or null if not found
 *
 * Note: For ambiguous mappings (e.g., CARD_BAR with SINGLE_SENSOR), this returns a default WidgetType.
 * Use getPossibleWidgetTypesFromChartSource() to get all possible matches.
 */
export function getWidgetTypeFromChartSource(
    chart: string | ChartType,
    source: string | ChartSource
): WidgetType | null {
    const key = `${chart ?? "".toUpperCase()}|${source ?? "".toUpperCase()}`;
    return chartSourceToWidgetTypeMap.get(key) ?? null;
}

export function getWidgetConfigFromChartSource(
    chart: string | ChartType,
    source: string | ChartSource
): WidgetConfigType | null {
    const key = `${chart ?? "".toUpperCase()}|${source ?? "".toUpperCase()}`;
    const widgetType = chartSourceToWidgetTypeMap.get(key);
    if (!widgetType) {
        return null;
    }
    return widgetConfig[widgetType] ?? null;
};


/**
 * Get chart and source from WidgetType
 * @param widgetType - WidgetType enum value
 * @returns ChartSourcePair with chart and source, or null if not found
 */
export function getChartSourceFromWidgetType(
    widgetType: WidgetType
): ChartSourcePair {
    return widgetTypeToChartSource[widgetType];
}

export type MeterConsumptionType = "energy" | "water";
export type MeterChartVariant = "single" | "combined";

export function getMeterChartVariantFromChartSource(
    chart: ChartType.BAR | ChartType.STACKED_BAR,
    source: ChartSource.SINGLE_SENSOR | ChartSource.MULTIPLE_SENSORS
): MeterChartVariant {
    return source === ChartSource.MULTIPLE_SENSORS || chart === ChartType.STACKED_BAR
        ? "combined"
        : "single";
}

export function getMeterWidgetTypeByVariant(
    variant: MeterChartVariant,
    consumptionType: MeterConsumptionType
): WidgetType {
    if (consumptionType === "energy") {
        return variant === "combined"
            ? WidgetType.METER_CHART_ENERGY_COMBINE_CONSUMPTION
            : WidgetType.METER_CHART_ENERGY_CONSUMPTION;
    }

    return variant === "combined"
        ? WidgetType.METER_CHART_WATER_COMBINE_CONSUMPTION
        : WidgetType.METER_CHART_WATER_CONSUMPTION;
}

/**
 * Get WidgetType for Meter consumption based on chart type and consumption type
 * @param chart - Chart type (BAR or STACKED_BAR)
 * @param source - Source type (SINGLE_SENSOR or MULTIPLE_SENSORS)
 * @param consumptionType - "energy" or "water"
 * @returns WidgetType for the meter consumption widget
 */
export function getMeterWidgetType(
    chart: ChartType.BAR | ChartType.STACKED_BAR,
    source: ChartSource.SINGLE_SENSOR | ChartSource.MULTIPLE_SENSORS,
    consumptionType: MeterConsumptionType
): WidgetType {
    const variant = getMeterChartVariantFromChartSource(chart, source);
    return getMeterWidgetTypeByVariant(variant, consumptionType);
}

